import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wallet, Send, ArrowUpRight, ArrowDownRight, Clock, 
  CheckCircle, AlertCircle, Home, Menu, X, Globe, User, LogOut,
  Plus, Users, CreditCard, Loader2, Repeat, Pause, Play, Trash2, Receipt, Info,
  QrCode, Store, Eye, Copy, Share2, ShoppingBag, Download, Bell, BellOff, Gift
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getPushSubscriptionStatus, requestNotificationPermission } from '../../utils/pushNotifications';
import ReferralProgram from '../../components/ReferralProgram';
import { DualCurrencyInline } from '../../components/DualCurrency';
import MobileBottomNav from '../../components/MobileBottomNav';
import BuyForFamily from '../../components/BuyForFamily';
import DeliveryTimeline from '../../components/DeliveryTimeline';
import ProfileSecuritySection from '../../components/ProfileSecuritySection';

import { API_URL } from '../../utils/apiConfig';

/**
 * Country codes for phone number input
 */
const COUNTRY_CODES = [
  { code: '+1', country: 'US/DR', flag: '🇺🇸', label: 'US / Dominican Republic (+1)' },
  { code: '+52', country: 'MX', flag: '🇲🇽', label: 'Mexico (+52)' },
  { code: '+57', country: 'CO', flag: '🇨🇴', label: 'Colombia (+57)' },
  { code: '+58', country: 'VE', flag: '🇻🇪', label: 'Venezuela (+58)' },
  { code: '+502', country: 'GT', flag: '🇬🇹', label: 'Guatemala (+502)' },
  { code: '+503', country: 'SV', flag: '🇸🇻', label: 'El Salvador (+503)' },
  { code: '+504', country: 'HN', flag: '🇭🇳', label: 'Honduras (+504)' },
  { code: '+505', country: 'NI', flag: '🇳🇮', label: 'Nicaragua (+505)' },
  { code: '+507', country: 'PA', flag: '🇵🇦', label: 'Panama (+507)' },
  { code: '+509', country: 'HT', flag: '🇭🇹', label: 'Haiti (+509)' },
  { code: '+34', country: 'ES', flag: '🇪🇸', label: 'Spain (+34)' },
];

/**
 * Format phone number based on selected country code
 */
const formatPhoneWithCountry = (value, countryCode) => {
  // Get only digits from input
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 0) return '';
  
  // Format based on country code length and phone number patterns
  if (countryCode === '+1') {
    // US/DR: +1 XXX XXX XXXX (10 digits)
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    }
  } else if (countryCode === '+52') {
    // Mexico: +52 XX XXXX XXXX (10 digits)
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6, 10)}`;
    }
  } else {
    // Generic format: XXX XXX XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    }
  }
};

/**
 * Normalize phone number for API submission
 * Combines country code with formatted number
 */
const normalizePhoneWithCountry = (value, countryCode) => {
  const digits = value.replace(/\D/g, '');
  return `${countryCode}${digits}`;
};

/**
 * Auto-format phone number to international format (legacy, kept for compatibility)
 */
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  
  if (digits.length <= 10) {
    if (digits.length <= 3) {
      return `+1 ${digits}`;
    } else if (digits.length <= 6) {
      return `+1 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    }
  }
  
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  }
  
  if (digits.length <= 12) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`.trim();
  }
  
  return `+${digits.slice(0, 15)}`;
};

/**
 * Normalize phone number for API submission (legacy)
 */
const normalizePhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return `+${digits}`;
};

/**
 * Phone Input with Country Selector Component
 */
const PhoneInputWithCountry = ({ 
  value, 
  onChange, 
  countryCode, 
  onCountryChange, 
  placeholder = "809 555 1234",
  required = false,
  id,
  label,
  helperText,
  language = 'en'
}) => {
  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];
  
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneWithCountry(e.target.value, countryCode);
    onChange(formatted);
  };
  
  return (
    <div className="space-y-1.5">
      {label && (
        <Label htmlFor={id}>
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <div className="flex gap-2">
        {/* Country Code Selector */}
        <Select value={countryCode} onValueChange={onCountryChange}>
          <SelectTrigger className="w-[140px] shrink-0">
            <SelectValue>
              <span className="flex items-center gap-2">
                <span>{selectedCountry.flag}</span>
                <span className="font-medium">{selectedCountry.code}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <span className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span>{country.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Phone Number Input */}
        <Input
          id={id}
          type="tel"
          placeholder={placeholder}
          value={value}
          onChange={handlePhoneChange}
          required={required}
          className="flex-1"
          data-testid="recipient-phone-input"
        />
      </div>
      {helperText && (
        <p className="text-xs text-slate-500">{helperText}</p>
      )}
    </div>
  );
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

export default function SenderDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [recurringTransfers, setRecurringTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendFamilyOpen, setSendFamilyOpen] = useState(false);
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');
  
  // Send to Family form
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientCountryCode, setRecipientCountryCode] = useState('+1'); // Country code for Send to Family
  // NOTE: recipientEmail is shared with Send-to-Colmado state declared below; keeping single declaration to avoid duplicate identifier compile error
  const [recipientFirstName, setRecipientFirstName] = useState('');  // NEW: First name for colmado authorizations
  const [sendAmount, setSendAmount] = useState('');
  const [sendDescription, setSendDescription] = useState('');
  
  // Add Funds form
  const [fundAmount, setFundAmount] = useState('');
  
  // Recurring form
  const [recurringPhone, setRecurringPhone] = useState('');
  const [recurringAmount, setRecurringAmount] = useState('');
  const [recurringFrequency, setRecurringFrequency] = useState('weekly');
  const [recurringDescription, setRecurringDescription] = useState('');

  // Pricing state
  const [priceQuote, setPriceQuote] = useState(null);  // For card payments (with fees)
  const [walletSendQuote, setWalletSendQuote] = useState(null);  // For wallet sends (no fees)
  const [priceLoading, setPriceLoading] = useState(false);

  // Controlled Redemption (Send to Colmado) state
  const [sendColmadoOpen, setSendColmadoOpen] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone2, setRecipientPhone2] = useState('');
  const [recipientCountryCode2, setRecipientCountryCode2] = useState('+1'); // Country code for Buy at Colmado
  const [colmadoAmount, setColmadoAmount] = useState('');
  const [colmadoQuote, setColmadoQuote] = useState(null);
  const [authorizationResult, setAuthorizationResult] = useState(null);
  const [myAuthorizations, setMyAuthorizations] = useState([]);
  const [viewAuthOpen, setViewAuthOpen] = useState(false);
  const [selectedAuth, setSelectedAuth] = useState(null);
  
  // New Buy Flow state
  const [buyFlowOpen, setBuyFlowOpen] = useState(false);
  const [buyAgainData, setBuyAgainData] = useState(null);  // { beneficiary, merchant, amount } for Buy Again
  
  // Push notifications state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  
  // Referral Program state
  const [referralOpen, setReferralOpen] = useState(false);

  const { language, toggleLanguage, t } = useLanguage();
  const { user, wallet, token, logout, refreshWallet } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Check push notification status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      if (token && isPushSupported()) {
        const status = await getPushSubscriptionStatus(token);
        setPushEnabled(status.subscribed && status.backendSubscribed);
      }
    };
    checkPushStatus();
  }, [token]);
  
  // Toggle push notifications
  const togglePushNotifications = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush(token);
        setPushEnabled(false);
        toast.success(language === 'es' ? 'Notificaciones desactivadas' : 'Notifications disabled');
      } else {
        const permission = await requestNotificationPermission();
        if (permission === 'granted') {
          const subscription = await subscribeToPush(token);
          if (subscription) {
            setPushEnabled(true);
            toast.success(language === 'es' ? 'Notificaciones activadas' : 'Notifications enabled');
          }
        } else if (permission === 'denied') {
          toast.error(language === 'es' ? 'Permiso denegado. Habilita notificaciones en configuración del navegador.' : 'Permission denied. Enable notifications in browser settings.');
        }
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cambiar notificaciones' : 'Failed to toggle notifications');
    } finally {
      setPushLoading(false);
    }
  };
  
  // Download authorization history
  const downloadHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supporter/authorization-history/export?language=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PeCash_History_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success(language === 'es' ? 'Historial descargado' : 'History downloaded');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al descargar' : 'Failed to download');
    }
  };

  // Fetch price quote when amount changes
  const fetchPriceQuote = useCallback(async (amount) => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) < 10) {
      setPriceQuote(null);
      setWalletSendQuote(null);
      return;
    }

    setPriceLoading(true);
    try {
      // Fetch both quotes in parallel
      const [cardResponse, walletResponse] = await Promise.all([
        fetch(`${API_URL}/api/pricing/quote?amount_usd=${parseFloat(amount)}`),
        fetch(`${API_URL}/api/pricing/quote?amount_usd=${parseFloat(amount)}&context=wallet_send`)
      ]);
      
      if (cardResponse.ok) {
        const cardData = await cardResponse.json();
        setPriceQuote(cardData);
      } else {
        setPriceQuote(null);
      }
      
      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        setWalletSendQuote(walletData);
      } else {
        setWalletSendQuote(null);
      }
    } catch (error) {
      setPriceQuote(null);
      setWalletSendQuote(null);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // Debounced price quote fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      if (sendAmount) {
        fetchPriceQuote(sendAmount);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [sendAmount, fetchPriceQuote]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (fundAmount) {
        fetchPriceQuote(fundAmount);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [fundAmount, fetchPriceQuote]);

  // Check for payment status from Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your balance.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          toast.success('Payment successful! Your balance has been updated.');
          refreshWallet();
          fetchTransactions();
          window.history.replaceState({}, '', '/app/sender');
          return;
        }
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status error:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transactions/my-transactions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecurringTransfers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recurring/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecurringTransfers(data.recurring_transfers || []);
      }
    } catch (error) {
      console.error('Failed to fetch recurring transfers:', error);
    }
  };

  // Fetch available merchants (Colmados)
  const fetchMerchants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merchants/active`);
      if (response.ok) {
        const data = await response.json();
        setMerchants(data.merchants || []);
      }
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    }
  };

  // Fetch user's authorizations
  const fetchMyAuthorizations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/authorizations/my-authorizations`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMyAuthorizations(data.authorizations || []);
      }
    } catch (error) {
      console.error('Failed to fetch authorizations:', error);
    }
  };

  // Fetch price quote for colmado amount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (colmadoAmount && parseFloat(colmadoAmount) >= 10) {
        fetchColmadoQuote(colmadoAmount);
      } else {
        setColmadoQuote(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [colmadoAmount]);

  const fetchColmadoQuote = async (amount) => {
    setPriceLoading(true);
    try {
      // Fetch both card and wallet quotes in parallel
      const [cardResponse, walletResponse] = await Promise.all([
        fetch(`${API_URL}/api/pricing/quote?amount_usd=${parseFloat(amount)}`),
        fetch(`${API_URL}/api/pricing/quote?amount_usd=${parseFloat(amount)}&context=wallet_send`)
      ]);
      
      if (cardResponse.ok) {
        const cardData = await cardResponse.json();
        setColmadoQuote(cardData);
      }
      
      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        setWalletSendQuote(walletData);
      }
    } catch (error) {
      console.error('Failed to fetch price quote:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  // Create Purchase Authorization (Controlled Redemption)
  const handleCreateAuthorization = async () => {
    if (!selectedMerchant || !recipientName.trim() || !colmadoAmount) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!recipientEmail || !recipientEmail.trim()) {
      toast.error(language === 'es' ? 'El correo electrónico del beneficiario es requerido' : 'Recipient email is required');
      return;
    }
    if (!emailRegex.test(recipientEmail.trim())) {
      toast.error(language === 'es' ? 'Por favor ingresa un correo electrónico válido' : 'Please enter a valid email address');
      return;
    }
    
    // Validate phone
    if (!recipientPhone2 || !recipientPhone2.trim()) {
      toast.error(language === 'es' ? 'El número de teléfono del beneficiario es requerido' : 'Recipient phone number is required');
      return;
    }
    if (recipientPhone2.trim().length < 5) {
      toast.error(language === 'es' ? 'Ingresa un número de teléfono válido' : 'Please enter a valid phone number');
      return;
    }

    const amount = parseFloat(colmadoAmount);
    if (amount < 10 || amount > 100) {
      toast.error('Amount must be between $10 and $100');
      return;
    }

    setSendLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/authorizations/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          merchant_id: selectedMerchant,
          amount_usd: amount,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim(),
          recipient_phone: normalizePhoneWithCountry(recipientPhone2, recipientCountryCode2),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t('authorization.authorizationCreated'));
        setAuthorizationResult(data);
        refreshWallet();
        fetchMyAuthorizations();
      } else {
        toast.error(data.detail || 'Failed to create authorization');
      }
    } catch (error) {
      toast.error('Failed to create authorization');
    } finally {
      setSendLoading(false);
    }
  };

  // Reset colmado form
  const resetColmadoForm = () => {
    setSelectedMerchant('');
    setRecipientName('');
    setRecipientEmail('');
    setRecipientPhone2('');
    setColmadoAmount('');
    setColmadoQuote(null);
    setAuthorizationResult(null);
  };

  // Handle viewing QR code from transaction list
  const handleViewTransactionQR = async (authorizationId) => {
    try {
      const response = await fetch(`${API_URL}/api/authorizations/${authorizationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedAuth(data.authorization);
        setViewAuthOpen(true);
      } else {
        toast.error(language === 'es' ? 'Error al cargar autorización' : 'Failed to load authorization');
      }
    } catch (error) {
      console.error('Failed to fetch authorization:', error);
      toast.error(language === 'es' ? 'Error al cargar autorización' : 'Failed to load authorization');
    }
  };

  useEffect(() => {
    if (token) {
      fetchTransactions();
      fetchRecurringTransfers();
      fetchMerchants();
      fetchMyAuthorizations();
    }
  }, [token]);

  const handleSendToFamily = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!recipientPhone.trim()) {
      toast.error(language === 'es' ? 'El número de teléfono es requerido' : 'Phone number is required');
      return;
    }
    if (!recipientEmail.trim()) {
      toast.error(language === 'es' ? 'El correo electrónico es requerido' : 'Email address is required');
      return;
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      toast.error(language === 'es' ? 'Por favor ingresa un correo electrónico válido' : 'Please enter a valid email address');
      return;
    }
    if (!recipientFirstName.trim()) {
      toast.error(language === 'es' ? 'El nombre del beneficiario es requerido' : 'Recipient first name is required');
      return;
    }
    
    setSendLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/transactions/send-to-family`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_phone: normalizePhoneWithCountry(recipientPhone, recipientCountryCode),
          recipient_email: recipientEmail.trim().toLowerCase(),
          recipient_name: recipientFirstName.trim(),
          amount: parseFloat(sendAmount),
          description: sendDescription || undefined,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Successfully sent $${sendAmount} to ${recipientFirstName}. Receipt: ${data.receipt_id}`);
        setSendFamilyOpen(false);
        setRecipientPhone('');
        setRecipientEmail('');
        setRecipientFirstName('');
        setSendAmount('');
        setSendDescription('');
        refreshWallet();
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create authorization');
      }
    } catch (error) {
      toast.error('Failed to create authorization. Please try again.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleAddFunds = async (e) => {
    e.preventDefault();
    setSendLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(fundAmount),
          origin_url: window.location.origin,
          recipient_type: 'self',
          description: 'Add funds to wallet',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Handle TEST_MODE response (no Stripe redirect)
        if (data.mode === 'TEST_MODE') {
          toast.success(`$${data.credited_amount.toFixed(2)} added to your wallet! New balance: $${data.new_balance.toFixed(2)}`);
          setAddFundsOpen(false);
          setFundAmount('');
          setPriceQuote(null);
          refreshWallet();
          fetchTransactions();
        } else if (data.checkout_url) {
          // Normal Stripe redirect
          window.location.href = data.checkout_url;
        } else {
          toast.error('Unexpected response from payment service');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate payment');
      }
    } catch (error) {
      toast.error('Failed to initiate payment. Please try again.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleSendToFamilyViaStripe = async () => {
    // Validate all required fields
    if (!recipientPhone.trim()) {
      toast.error(language === 'es' ? 'El número de teléfono es requerido' : 'Phone number is required');
      return;
    }
    if (!recipientEmail.trim()) {
      toast.error(language === 'es' ? 'El correo electrónico es requerido' : 'Email address is required');
      return;
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      toast.error(language === 'es' ? 'Por favor ingresa un correo electrónico válido' : 'Please enter a valid email address');
      return;
    }
    if (!recipientFirstName.trim()) {
      toast.error(language === 'es' ? 'El nombre del beneficiario es requerido' : 'Recipient first name is required');
      return;
    }
    if (!sendAmount) {
      toast.error(language === 'es' ? 'El monto es requerido' : 'Amount is required');
      return;
    }

    setSendLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(sendAmount),
          origin_url: window.location.origin,
          recipient_type: 'family',
          recipient_id: normalizePhoneWithCountry(recipientPhone, recipientCountryCode),
          recipient_phone: normalizePhoneWithCountry(recipientPhone, recipientCountryCode),
          recipient_email: recipientEmail.trim().toLowerCase(),
          recipient_name: recipientFirstName.trim(),
          description: sendDescription || `Transfer to ${recipientFirstName.trim()}`,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Handle TEST_MODE response (no Stripe redirect)
        if (data.mode === 'TEST_MODE') {
          toast.success(`$${data.credited_amount.toFixed(2)} sent to ${recipientFirstName}!`);
          setSendFamilyOpen(false);
          setRecipientPhone('');
          setRecipientEmail('');
          setRecipientFirstName('');
          setSendAmount('');
          setSendDescription('');
          setPriceQuote(null);
          refreshWallet();
          fetchTransactions();
        } else if (data.checkout_url) {
          // Normal Stripe redirect
          window.location.href = data.checkout_url;
        } else {
          toast.error('Unexpected response from payment service');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate payment');
      }
    } catch (error) {
      toast.error('Failed to initiate payment. Please try again.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    setSendLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/recurring/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_phone: recurringPhone,
          amount: parseFloat(recurringAmount),
          frequency: recurringFrequency,
          description: recurringDescription || undefined,
        }),
      });

      if (response.ok) {
        toast.success('Scheduled authorization created successfully');
        setRecurringOpen(false);
        setRecurringPhone('');
        setRecurringAmount('');
        setRecurringFrequency('weekly');
        setRecurringDescription('');
        fetchRecurringTransfers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create scheduled authorization');
      }
    } catch (error) {
      toast.error('Failed to create scheduled authorization');
    } finally {
      setSendLoading(false);
    }
  };

  const handleToggleRecurring = async (recurringId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    
    try {
      const response = await fetch(`${API_URL}/api/recurring/${recurringId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success(`Scheduled authorization ${newStatus === 'active' ? 'resumed' : 'paused'}`);
        fetchRecurringTransfers();
      }
    } catch (error) {
      toast.error('Failed to update scheduled authorization');
    }
  };

  const handleCancelRecurring = async (recurringId) => {
    if (!confirm('Are you sure you want to cancel this scheduled authorization?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/recurring/${recurringId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Scheduled authorization cancelled');
        fetchRecurringTransfers();
      }
    } catch (error) {
      toast.error('Failed to cancel scheduled authorization');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-600" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'active':
      case 'succeeded':
        return 'bg-emerald-50 text-emerald-700';
      case 'pending':
      case 'paused':
      case 'pending_redemption':
        return 'bg-amber-50 text-amber-700';
      case 'failed':
      case 'cancelled':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-slate-50 text-slate-700';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTransactionIcon = (type) => {
    if (type?.includes('topup') || type === 'wallet_topup') {
      return <Plus className="w-5 h-5 text-emerald-600" />;
    }
    if (type?.includes('family') || type?.includes('recurring')) {
      return <Users className="w-5 h-5 text-blue-600" />;
    }
    if (type === 'colmado_authorization' || type?.includes('colmado')) {
      return <Store className="w-5 h-5 text-amber-600" />;
    }
    return <ArrowUpRight className="w-5 h-5 text-slate-600" />;
  };

  const activeRecurring = recurringTransfers.filter(r => r.status === 'active').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sender-dashboard">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="font-bold text-xl text-slate-900">PeCash</span>
          </Link>

          <nav className="space-y-2">
            <Link
              to="/app/sender"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 font-medium"
              data-testid="sidebar-dashboard"
            >
              <Wallet className="w-5 h-5" />
              {t('dashboard.sender.title')}
            </Link>
            <button
              onClick={() => setSendFamilyOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-send-family"
            >
              <Users className="w-5 h-5" />
              Send to Family
            </button>
            <button
              onClick={() => { setSendColmadoOpen(true); resetColmadoForm(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-send-colmado"
            >
              <Send className="w-5 h-5" />
              {t('authorization.sendToColmado')}
            </button>
            <button
              onClick={() => setRecurringOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-recurring"
            >
              <Repeat className="w-5 h-5" />
              Scheduled Authorizations
              {activeRecurring > 0 && (
                <span className="ml-auto bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                  {activeRecurring}
                </span>
              )}
            </button>
            <Link
              to="/app/sender"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-home"
            >
              <Home className="w-5 h-5" />
              {t('nav.home')}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900 text-sm">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          
          {/* Push Notifications Toggle */}
          <Button 
            variant="outline" 
            className={`w-full justify-start mb-2 ${pushEnabled ? 'text-emerald-600 border-emerald-200' : 'text-slate-600'}`}
            onClick={togglePushNotifications}
            disabled={pushLoading}
            data-testid="toggle-push-btn"
          >
            {pushLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : pushEnabled ? (
              <Bell className="w-4 h-4 mr-2" />
            ) : (
              <BellOff className="w-4 h-4 mr-2" />
            )}
            {language === 'es' 
              ? (pushEnabled ? 'Notificaciones Activas' : 'Activar Notificaciones')
              : (pushEnabled ? 'Notifications On' : 'Enable Notifications')}
          </Button>
          
          {/* Download History */}
          <Button 
            variant="outline" 
            className="w-full justify-start mb-2 text-slate-600"
            onClick={downloadHistory}
            data-testid="download-history-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Descargar Historial' : 'Download History'}
          </Button>
          
          {/* Referral Program */}
          <Button 
            variant="outline" 
            className="w-full justify-start mb-2 text-purple-600 border-purple-200 hover:bg-purple-50"
            onClick={() => setReferralOpen(true)}
            data-testid="referral-program-btn"
          >
            <Gift className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Programa de Referidos' : 'Referral Program'}
          </Button>
          
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600" 
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-200 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="mobile-sidebar-toggle"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-xl font-bold text-slate-900">{t('dashboard.sender.title')}</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                data-testid="dashboard-language-toggle"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span>{language.toUpperCase()}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-6">
          <motion.div
            initial="initial"
            animate="animate"
            variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
          >
            {/* Welcome */}
            <motion.div variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">
                {t('dashboard.welcome')}, {user?.name?.split(' ')[0] || user?.email?.split('@')[0]}
              </h2>
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Authorization Limit */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="authorization-limit-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">{t('dashboard.authorizationLimit')}</span>
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900">${(wallet?.balance || 0).toFixed(2)}</p>
                <Button
                  onClick={() => setAddFundsOpen(true)}
                  variant="outline"
                  size="sm"
                  className="mt-3 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  data-testid="add-funds-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Funds
                </Button>
              </div>

              {/* Quick Authorization for Family */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 shadow-sm text-white" data-testid="authorize-family-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-blue-100">Authorize Purchase</span>
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                </div>
                <p className="text-lg font-medium text-blue-100 mb-3">
                  {language === 'es' 
                    ? 'Apoya a tu familia en RD'
                    : 'Support your family in DR'}
                </p>
                <Button
                  onClick={() => setSendFamilyOpen(true)}
                  className="bg-white text-blue-700 hover:bg-blue-50 rounded-full"
                  data-testid="quick-send-family-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Send Now
                </Button>
              </div>

              {/* Scheduled Authorizations */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="recurring-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">Scheduled</span>
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Repeat className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900">{activeRecurring}</p>
                <p className="text-sm text-slate-500">Active scheduled authorizations</p>
              </div>

              {/* Total Transactions */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="total-transactions-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">Transactions</span>
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900">{transactions.length}</p>
              </div>
            </motion.div>

            {/* Tab Switcher */}
            <motion.div variants={fadeInUp} className="flex gap-2 mb-4 flex-wrap">
              <Button
                variant={activeTab === 'transactions' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('transactions')}
                className={activeTab === 'transactions' ? 'bg-emerald-600' : ''}
              >
                Transactions
              </Button>
              <Button
                variant={activeTab === 'recurring' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('recurring')}
                className={activeTab === 'recurring' ? 'bg-emerald-600' : ''}
              >
                Scheduled Authorizations
              </Button>
              <Button
                variant={activeTab === 'authorizations' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('authorizations')}
                className={activeTab === 'authorizations' ? 'bg-emerald-600' : ''}
                data-testid="authorizations-tab"
              >
                <Store className="w-4 h-4 mr-1" />
                {t('authorization.myAuthorizations')}
                {myAuthorizations.filter(a => a.status === 'authorized_pending_redemption' || a.status === 'partially_redeemed').length > 0 && (
                  <span className="ml-2 bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {myAuthorizations.filter(a => a.status === 'authorized_pending_redemption' || a.status === 'partially_redeemed').length}
                  </span>
                )}
              </Button>
              <Button
                variant={activeTab === 'profile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('profile')}
                className={activeTab === 'profile' ? 'bg-purple-600' : ''}
                data-testid="profile-tab"
              >
                <User className="w-4 h-4 mr-1" />
                {language === 'es' ? 'Perfil y Seguridad' : 'Profile & Security'}
              </Button>
            </motion.div>

            {/* Recent Transactions */}
            {activeTab === 'transactions' && (
              <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="transactions-table">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">{t('dashboard.recentTransactions')}</h3>
                </div>
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No transactions yet</p>
                    <p className="text-sm">Add funds or create an authorization to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-slate-50 transition-colors" data-testid={`transaction-${tx.id}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                  {getTransactionIcon(tx.transaction_type)}
                                </div>
                                <div>
                                  <span className="font-medium text-slate-900 capitalize">
                                    {tx.transaction_type?.replace(/_/g, ' ') || 'Transaction'}
                                  </span>
                                  {tx.recipient_phone && (
                                    <p className="text-sm text-slate-500">{tx.recipient_phone}</p>
                                  )}
                                  {tx.merchant_name && (
                                    <p className="text-sm text-slate-500">{tx.merchant_name}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`font-semibold ${tx.transaction_type?.includes('topup') ? 'text-emerald-600' : 'text-slate-900'}`}>
                                {tx.transaction_type?.includes('topup') ? '+' : '-'}${tx.amount?.toFixed(2)}
                              </span>
                              {tx.dop_amount && (
                                <p className="text-xs text-slate-500">RD${tx.dop_amount?.toLocaleString()}</p>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                                {getStatusIcon(tx.status)}
                                {tx.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {formatDate(tx.created_at)}
                            </td>
                            <td className="px-6 py-4">
                              {/* Show QR icon ONLY for colmado_authorization transactions */}
                              {tx.authorization_id && tx.transaction_type === 'colmado_authorization' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewTransactionQR(tx.authorization_id)}
                                  data-testid={`view-qr-${tx.id}`}
                                  title={language === 'es' ? 'Ver código QR' : 'View QR Code'}
                                >
                                  <QrCode className="w-4 h-4 text-emerald-600" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* Scheduled Authorizations List */}
            {activeTab === 'recurring' && (
              <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="recurring-table">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Scheduled Authorizations</h3>
                  <Button
                    onClick={() => setRecurringOpen(true)}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> New Scheduled
                  </Button>
                </div>
                {recurringTransfers.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Repeat className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No scheduled authorizations</p>
                    <p className="text-sm">Set up automatic authorizations for your beneficiaries</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Beneficiary</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Frequency</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Next Run</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {recurringTransfers.map((rec) => (
                          <tr key={rec.id} className="hover:bg-slate-50 transition-colors" data-testid={`recurring-${rec.recurring_id}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                  <Repeat className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                  <span className="font-medium text-slate-900">{rec.recipient_phone}</span>
                                  <p className="text-sm text-slate-500">{rec.description}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-semibold text-slate-900">${rec.amount?.toFixed(2)}</span>
                            </td>
                            <td className="px-6 py-4 capitalize text-slate-600">{rec.frequency}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(rec.status)}`}>
                                {rec.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {rec.status !== 'cancelled' ? formatDate(rec.next_execution) : '-'}
                            </td>
                            <td className="px-6 py-4">
                              {rec.status !== 'cancelled' && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleRecurring(rec.recurring_id, rec.status)}
                                    title={rec.status === 'active' ? 'Pause' : 'Resume'}
                                  >
                                    {rec.status === 'active' ? (
                                      <Pause className="w-4 h-4 text-amber-600" />
                                    ) : (
                                      <Play className="w-4 h-4 text-emerald-600" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCancelRecurring(rec.recurring_id)}
                                    title="Cancel"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* My Authorizations Tab */}
            {activeTab === 'authorizations' && (
              <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="authorizations-table">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">{t('authorization.myAuthorizations')}</h3>
                  <Button
                    onClick={() => { setSendColmadoOpen(true); resetColmadoForm(); }}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="new-authorization-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> {language === 'es' ? 'Nueva' : 'New'}
                  </Button>
                </div>
                {myAuthorizations.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Store className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>{language === 'es' ? 'No hay autorizaciones' : 'No authorizations yet'}</p>
                    <p className="text-sm">{language === 'es' ? 'Envía a un colmado para comenzar' : 'Send to a colmado to get started'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('authorization.referenceId')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{language === 'es' ? 'Colmado' : 'Colmado'}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('authorization.authorizedAmount')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('authorization.remaining')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('authorization.expiresAt')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {myAuthorizations.map((auth) => (
                          <tr key={auth.id} className="hover:bg-slate-50 transition-colors" data-testid={`authorization-${auth.reference_id}`}>
                            <td className="px-6 py-4">
                              <span className="font-mono text-sm font-medium text-slate-900">{auth.reference_id}</span>
                              <p className="text-xs text-slate-500">{auth.recipient_label}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-900">{auth.merchant_name}</td>
                            <td className="px-6 py-4">
                              <DualCurrencyInline 
                                usd={auth.usd_amount} 
                                dop={auth.dop_authorized_amount} 
                                role="sender"
                                className="font-semibold text-slate-900"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <span className={`font-semibold ${auth.dop_remaining_amount > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                RD${auth.dop_remaining_amount?.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                auth.status === 'authorized_pending_redemption' ? 'bg-amber-50 text-amber-700' :
                                auth.status === 'partially_redeemed' ? 'bg-blue-50 text-blue-700' :
                                auth.status === 'fully_redeemed' ? 'bg-emerald-50 text-emerald-700' :
                                'bg-red-50 text-red-700'
                              }`}>
                                {auth.status === 'authorized_pending_redemption' && <Clock className="w-3 h-3" />}
                                {auth.status === 'partially_redeemed' && <ArrowDownRight className="w-3 h-3" />}
                                {auth.status === 'fully_redeemed' && <CheckCircle className="w-3 h-3" />}
                                {auth.status === 'authorized_pending_redemption' ? t('authorization.statusPending') :
                                 auth.status === 'partially_redeemed' ? t('authorization.statusPartial') :
                                 auth.status === 'fully_redeemed' ? t('authorization.statusComplete') :
                                 t('authorization.statusExpired')}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {new Date(auth.expires_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setSelectedAuth(auth); setViewAuthOpen(true); }}
                                  data-testid={`view-auth-${auth.reference_id}`}
                                >
                                  <Eye className="w-4 h-4 text-slate-600" />
                                </Button>
                                {/* Buy Again Button */}
                                {auth.status !== 'expired' && auth.status !== 'cancelled' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      // Pre-fill Buy Again data from this authorization
                                      setBuyAgainData({
                                        beneficiary: {
                                          id: auth.beneficiary_id,
                                          name: auth.recipient_label,
                                          email: auth.beneficiary_email,
                                          phone: auth.beneficiary_phone,
                                        },
                                        merchant: merchants.find(m => m.id === auth.merchant_id) || {
                                          id: auth.merchant_id,
                                          business_name: auth.merchant_name,
                                        },
                                        amount: auth.usd_amount,
                                      });
                                      setBuyFlowOpen(true);
                                    }}
                                    title={language === 'es' ? 'Comprar de nuevo' : 'Buy Again'}
                                    data-testid={`buy-again-${auth.reference_id}`}
                                  >
                                    <Repeat className="w-4 h-4 text-purple-600" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* Profile & Security Tab */}
            {activeTab === 'profile' && (
              <motion.div variants={fadeInUp} data-testid="profile-security-section">
                <ProfileSecuritySection />
              </motion.div>
            )}
          </motion.div>
        </main>
      </div>

      {/* Send to Family Dialog */}
      <Dialog open={sendFamilyOpen} onOpenChange={(open) => { setSendFamilyOpen(open); if (!open) { setPriceQuote(null); setSendAmount(''); setRecipientEmail(''); setRecipientFirstName(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {t('pricing.youPay') || 'Send to Family'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Apoya a tu familia con compras en colmados participantes en República Dominicana.'
                : 'Support your family with purchases at participating colmados in the Dominican Republic.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendToFamily} className="space-y-4">
            {/* Recipient First Name */}
            <div>
              <Label htmlFor="recipientFirstName">
                {language === 'es' ? 'Nombre del beneficiario' : 'Recipient First Name'} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="recipientFirstName"
                type="text"
                placeholder={language === 'es' ? 'María' : 'Maria'}
                value={recipientFirstName}
                onChange={(e) => setRecipientFirstName(e.target.value)}
                required
                maxLength={50}
                data-testid="recipient-first-name-input"
              />
            </div>
            
            {/* Recipient Phone Number with Country Selector */}
            <PhoneInputWithCountry
              id="phone"
              value={recipientPhone}
              onChange={setRecipientPhone}
              countryCode={recipientCountryCode}
              onCountryChange={setRecipientCountryCode}
              placeholder="809 555 1234"
              required={true}
              label={language === 'es' ? 'Número de teléfono del beneficiario' : 'Recipient Phone Number'}
              helperText={language === 'es' 
                ? 'Selecciona el país y escribe el número'
                : 'Select country and enter number'}
              language={language}
            />
            
            {/* Recipient Email Address - NEW REQUIRED FIELD */}
            <div>
              <Label htmlFor="recipientEmailFamily">
                {language === 'es' ? 'Correo electrónico del beneficiario' : 'Recipient Email Address'} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="recipientEmailFamily"
                type="email"
                placeholder={language === 'es' ? 'ejemplo@correo.com' : 'example@email.com'}
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                required
                data-testid="recipient-email-family-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {language === 'es' 
                  ? 'Enviaremos la confirmación y recibos a este correo.'
                  : "We'll send confirmation and receipts to this email."}
              </p>
            </div>
            
            {/* Amount */}
            <div>
              <Label htmlFor="amount">{t('pricing.amount') || 'Amount'} (USD) <span className="text-red-500">*</span></Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="10"
                max="100"
                placeholder="25.00"
                value={sendAmount}
                onChange={(e) => setSendAmount(e.target.value)}
                required
                data-testid="send-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('pricing.min') || 'Minimum'}: $10 · {t('pricing.max') || 'Maximum'}: $100
              </p>
            </div>
            
            {/* Note (optional) */}
            <div>
              <Label htmlFor="description">{language === 'es' ? 'Nota (opcional)' : 'Note (optional)'}</Label>
              <Input
                id="description"
                type="text"
                placeholder={language === 'es' ? 'Para compras' : 'For groceries'}
                value={sendDescription}
                onChange={(e) => setSendDescription(e.target.value)}
                data-testid="send-description-input"
              />
            </div>

            {/* Pricing Breakdown */}
            {priceLoading && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            )}
            
            {/* Show wallet send breakdown (NO FEES) when user has sufficient balance */}
            {walletSendQuote && !priceLoading && wallet?.balance >= parseFloat(sendAmount || 0) && parseFloat(sendAmount || 0) >= 10 && (
              <div className="bg-emerald-50 rounded-xl p-4 space-y-2 border border-emerald-200" data-testid="wallet-send-breakdown">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium">
                    <Wallet className="w-4 h-4" />
                    {language === 'es' ? 'Envío desde billetera' : 'Wallet Send'}
                  </div>
                  {/* Savings badge */}
                  {priceQuote && (
                    <span className="bg-emerald-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                      {language === 'es' ? 'Ahorra' : 'Save'} ${(priceQuote.total_to_pay - walletSendQuote.total_to_pay).toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.amount') || 'Amount'}</span>
                  <span className="font-medium">${walletSendQuote.amount_usd.toFixed(2)}</span>
                </div>
                <div className="border-t border-emerald-200 pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>{t('pricing.youPay') || 'You pay'}</span>
                    <span className="text-emerald-600">${walletSendQuote.total_to_pay.toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                  <CheckCircle className="w-3 h-3" />
                  {language === 'es' ? 'Sin comisiones adicionales al enviar desde tu billetera' : 'No additional fees when sending from your wallet'}
                </p>
              </div>
            )}
            
            {/* Show card payment breakdown (WITH FEES) when user needs to pay by card */}
            {priceQuote && !priceLoading && (wallet?.balance < parseFloat(sendAmount || 0) || parseFloat(sendAmount || 0) < 10) && parseFloat(sendAmount || 0) >= 10 && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-2" data-testid="card-pricing-breakdown">
                <div className="flex items-center gap-2 text-slate-700 text-sm font-medium mb-2">
                  <CreditCard className="w-4 h-4" />
                  {language === 'es' ? 'Pago con tarjeta' : 'Card Payment'}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.amount') || 'Amount'}</span>
                  <span className="font-medium">${priceQuote.amount_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.serviceFee') || 'PeCashGlobal service fee'}</span>
                  <span className="font-medium">${priceQuote.service_fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.processingFee') || 'Card processing fee'}</span>
                  <span className="font-medium">${priceQuote.processing_fee.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>{t('pricing.youPay') || 'You pay'}</span>
                    <span className="text-emerald-600">${priceQuote.total_to_pay.toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('pricing.exchangeRate') || 'Exchange rate'}</span>
                    <span className="font-medium">1 USD = {priceQuote.exchange_rate} DOP</span>
                  </div>
                  <div className="flex justify-between font-semibold text-blue-600 mt-1">
                    <span>{t('pricing.colmadoReceives') || 'Recipient receives'}</span>
                    <span>{priceQuote.colmado_receives_dop.toLocaleString()} DOP</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-2">
                  <Info className="w-3 h-3" />
                  {t('pricing.rateNote') || 'Exchange rate applied at time of payment'}
                </p>
              </div>
            )}
            
            {wallet?.balance >= parseFloat(sendAmount || 0) && parseFloat(sendAmount || 0) >= 10 ? (
              <Button
                type="submit"
                disabled={sendLoading || !recipientPhone.trim() || !recipientEmail.trim() || !recipientFirstName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="send-from-wallet-btn"
              >
                {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Wallet className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Enviar' : 'Send'} ${parseFloat(sendAmount).toFixed(2)} 
                    <span className="text-emerald-200 ml-1 text-xs">({language === 'es' ? 'sin comisiones' : 'no fees'})</span>
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSendToFamilyViaStripe}
                disabled={sendLoading || !recipientPhone.trim() || !recipientEmail.trim() || !recipientFirstName.trim() || !sendAmount || parseFloat(sendAmount) < 10 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="send-via-card-btn"
              >
                {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Pagar con tarjeta' : 'Pay with Card'}
                    {priceQuote && ` - $${priceQuote.total_to_pay.toFixed(2)}`}
                  </>
                )}
              </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Funds Dialog */}
      <Dialog open={addFundsOpen} onOpenChange={(open) => { setAddFundsOpen(open); if (!open) { setPriceQuote(null); setFundAmount(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              {language === 'es' ? 'Agregar fondos' : 'Add Funds to Wallet'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Agrega dinero a tu billetera PeCash usando tu tarjeta.'
                : 'Add money to your PeCash wallet using your card.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddFunds} className="space-y-4">
            <div>
              <Label htmlFor="fundAmount">{t('pricing.amount') || 'Amount'} (USD)</Label>
              <Input
                id="fundAmount"
                type="number"
                step="0.01"
                min="10"
                max="100"
                placeholder="50.00"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                required
                data-testid="fund-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('pricing.min') || 'Minimum'}: $10 · {t('pricing.max') || 'Maximum'}: $100
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[25, 50, 100].map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant="outline"
                  onClick={() => setFundAmount(amount.toString())}
                  className={fundAmount === amount.toString() ? 'border-emerald-600 bg-emerald-50' : ''}
                >
                  ${amount}
                </Button>
              ))}
            </div>

            {/* Pricing Breakdown */}
            {priceLoading && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            )}
            
            {priceQuote && !priceLoading && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-2" data-testid="pricing-breakdown-funds">
                <div className="flex items-center gap-2 text-slate-700 text-sm font-medium mb-2">
                  <CreditCard className="w-4 h-4" />
                  {language === 'es' ? 'Recarga con tarjeta' : 'Card Top-up'}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.amount') || 'Amount to add'}</span>
                  <span className="font-medium">${priceQuote.amount_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.serviceFee') || 'PeCashGlobal service fee'}</span>
                  <span className="font-medium">${priceQuote.service_fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.processingFee') || 'Card processing fee'}</span>
                  <span className="font-medium">${priceQuote.processing_fee.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>{t('pricing.youPay') || 'You pay'}</span>
                    <span className="text-emerald-600">${priceQuote.total_to_pay.toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                  <CheckCircle className="w-3 h-3" />
                  {language === 'es' 
                    ? 'Sin comisiones adicionales al enviar desde tu billetera' 
                    : 'No fees when sending from your wallet'}
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={sendLoading || !fundAmount || parseFloat(fundAmount) < 10}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-funds-submit-btn"
            >
              {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Agregar' : 'Add'} ${priceQuote ? priceQuote.total_to_pay.toFixed(2) : (fundAmount || '0')}
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Scheduled Authorization Dialog */}
      <Dialog open={recurringOpen} onOpenChange={setRecurringOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-purple-600" />
              Create Scheduled Authorization
            </DialogTitle>
            <DialogDescription>
              Set up automatic purchase authorizations for your beneficiaries.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRecurring} className="space-y-4">
            <div>
              <Label htmlFor="recurringPhone">Beneficiary Phone Number</Label>
              <Input
                id="recurringPhone"
                type="tel"
                placeholder="+1 809 555 1234"
                value={recurringPhone}
                onChange={(e) => setRecurringPhone(e.target.value)}
                required
                data-testid="recurring-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="recurringAmount">Amount (USD)</Label>
              <Input
                id="recurringAmount"
                type="number"
                step="0.01"
                min="1"
                placeholder="50.00"
                value={recurringAmount}
                onChange={(e) => setRecurringAmount(e.target.value)}
                required
                data-testid="recurring-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                <SelectTrigger data-testid="recurring-frequency-select">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="recurringDescription">Note (optional)</Label>
              <Input
                id="recurringDescription"
                type="text"
                placeholder="For weekly groceries"
                value={recurringDescription}
                onChange={(e) => setRecurringDescription(e.target.value)}
                data-testid="recurring-description-input"
              />
            </div>
            <Button
              type="submit"
              disabled={sendLoading || !recurringPhone || !recurringAmount}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="create-recurring-btn"
            >
              {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Repeat className="w-4 h-4 mr-2" />
                  Create Scheduled Authorization
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send to Colmado Dialog */}
      <Dialog open={sendColmadoOpen} onOpenChange={(open) => { 
        setSendColmadoOpen(open); 
        if (!open) resetColmadoForm(); 
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-emerald-600" />
              {t('authorization.sendToColmado')}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Autoriza una compra en un colmado. El destinatario mostrará el código QR para recibir productos.'
                : 'Authorize a purchase at a colmado. The recipient will show the QR code to receive goods.'}
            </DialogDescription>
          </DialogHeader>

          {/* Show result if authorization was created */}
          {authorizationResult ? (
            <div className="space-y-4" data-testid="authorization-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-semibold text-emerald-800">{t('authorization.authorizationCreated')}</p>
              </div>

              {/* QR Code Display */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-6 text-center">
                {/* Generate QR with URL that opens app/web page */}
                <div className="flex justify-center mb-4">
                  <QRCode 
                    value={`${window.location.origin}/redeem/${authorizationResult.redemption?.token_value || authorizationResult.redemption?.numeric_code || authorizationResult.authorization?.reference_id}`}
                    size={192}
                    level="M"
                    data-testid="authorization-qr-code"
                  />
                </div>
                <p className="text-sm text-slate-600 mb-2">{t('authorization.qrInstructions')}</p>
                
                {/* Numeric backup code */}
                <div className="bg-slate-100 rounded-lg p-3 mt-3">
                  <p className="text-xs text-slate-500 mb-1">{t('authorization.numericCode') || 'Backup Code'}</p>
                  <p className="font-mono text-2xl font-bold tracking-widest text-slate-900" data-testid="numeric-code">
                    {authorizationResult.redemption?.numeric_code}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(authorizationResult.redemption?.numeric_code);
                      toast.success(language === 'es' ? 'Código copiado' : 'Code copied');
                    }}
                    className="mt-2"
                  >
                    <Copy className="w-4 h-4 mr-1" /> {language === 'es' ? 'Copiar' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Authorization Details */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('authorization.referenceId')}</span>
                  <span className="font-mono font-medium">{authorizationResult.authorization?.reference_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{language === 'es' ? 'Colmado' : 'Colmado'}</span>
                  <span className="font-medium">{authorizationResult.authorization?.merchant_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('authorization.recipientName')}</span>
                  <span className="font-medium">{authorizationResult.authorization?.recipient_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.amount')}</span>
                  <span className="font-medium">${authorizationResult.authorization?.usd_amount?.toFixed(2)} USD</span>
                </div>
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>{t('authorization.authorizedAmount')}</span>
                    <span className="text-emerald-600">
                      RD${authorizationResult.authorization?.dop_authorized_amount?.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{language === 'es' ? 'Válido hasta' : 'Valid until'}</span>
                  <span className="font-medium text-amber-600">
                    {new Date(authorizationResult.authorization?.authorization_expires_at || authorizationResult.authorization?.expires_at).toLocaleDateString()} 
                  </span>
                </div>
              </div>

              {/* Notification Status */}
              {authorizationResult.notifications && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    {language === 'es' ? 'Notificaciones enviadas' : 'Notifications sent'}
                  </p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-600">{language === 'es' ? 'Correo electrónico' : 'Email'}</span>
                      <span className="text-blue-800 font-medium">{authorizationResult.notifications.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600">SMS</span>
                      <span className="text-blue-800 font-medium">{authorizationResult.notifications.sms}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Beneficiary Secure Link */}
              {authorizationResult.redemption?.beneficiary_link && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-emerald-800 mb-2">
                    {language === 'es' ? 'Enlace seguro del beneficiario' : 'Beneficiary Secure Link'}
                  </p>
                  <p className="text-xs text-emerald-600 mb-2">
                    {language === 'es' 
                      ? 'Comparte este enlace con tu beneficiario. Podrán ver su saldo y código QR.'
                      : 'Share this link with your beneficiary. They can view their balance and QR code.'}
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      value={authorizationResult.redemption.beneficiary_link}
                      readOnly
                      className="text-xs bg-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(authorizationResult.redemption.beneficiary_link);
                        toast.success(language === 'es' ? 'Enlace copiado' : 'Link copied');
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSendColmadoOpen(false);
                    resetColmadoForm();
                  }}
                >
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    resetColmadoForm();
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> {language === 'es' ? 'Nueva Autorización' : 'New Authorization'}
                </Button>
              </div>
            </div>
          ) : (
            /* Form for creating authorization */
            <div className="space-y-4">
              <div>
                <Label htmlFor="selectColmado">{t('authorization.selectColmado')}</Label>
                <Select value={selectedMerchant} onValueChange={setSelectedMerchant}>
                  <SelectTrigger data-testid="select-colmado">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar colmado...' : 'Select colmado...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {merchants.map((merchant) => (
                      <SelectItem key={merchant.id} value={merchant.id}>
                        {merchant.business_name || merchant.name || merchant.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {merchants.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {language === 'es' ? 'No hay colmados disponibles' : 'No colmados available'}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="recipientName">{t('authorization.recipientName')}</Label>
                <Input
                  id="recipientName"
                  type="text"
                  placeholder={t('authorization.recipientNamePlaceholder')}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  maxLength={50}
                  data-testid="recipient-name-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {language === 'es' ? 'El colmado verificará este nombre' : 'The colmado will verify this name'}
                </p>
              </div>

              {/* NEW: Beneficiary Email */}
              <div>
                <Label htmlFor="recipientEmail">
                  {language === 'es' ? 'Correo electrónico del beneficiario' : 'Recipient Email Address'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="recipientEmail"
                  type="email"
                  placeholder={language === 'es' ? 'ejemplo@correo.com' : 'example@email.com'}
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  required
                  data-testid="recipient-email-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {language === 'es' 
                    ? 'Enviaremos al beneficiario su enlace seguro de compra y recibos por correo electrónico.' 
                    : "We'll send the beneficiary their secure purchase link and receipts by email."}
                </p>
              </div>

              {/* NEW: Beneficiary Phone with Country Selector */}
              <PhoneInputWithCountry
                id="recipientPhone2"
                value={recipientPhone2}
                onChange={setRecipientPhone2}
                countryCode={recipientCountryCode2}
                onCountryChange={setRecipientCountryCode2}
                placeholder="809 555 1234"
                required={true}
                label={language === 'es' ? 'Número de teléfono del beneficiario' : 'Recipient Phone Number'}
                helperText={language === 'es' 
                  ? 'Selecciona el país y escribe el número'
                  : 'Select country and enter number'}
                language={language}
              />

              <div>
                <Label htmlFor="colmadoAmount">{t('authorization.amountUSD')}</Label>
                <Input
                  id="colmadoAmount"
                  type="number"
                  step="0.01"
                  min="10"
                  max="100"
                  placeholder="25.00"
                  value={colmadoAmount}
                  onChange={(e) => setColmadoAmount(e.target.value)}
                  data-testid="colmado-amount-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {t('pricing.min')}: $10 · {t('pricing.max')}: $100
                </p>
              </div>

              {/* Pricing Breakdown */}
              {priceLoading && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              )}

              {/* Show wallet send breakdown (NO FEES) when user has sufficient balance */}
              {walletSendQuote && !priceLoading && wallet?.balance >= parseFloat(colmadoAmount || 0) && parseFloat(colmadoAmount || 0) >= 10 && (
                <div className="bg-emerald-50 rounded-xl p-4 space-y-2 border border-emerald-200" data-testid="colmado-wallet-breakdown">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium">
                      <Wallet className="w-4 h-4" />
                      {language === 'es' ? 'Pago desde billetera' : 'Wallet Payment'}
                    </div>
                    {/* Savings badge */}
                    {colmadoQuote && (
                      <span className="bg-emerald-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        {language === 'es' ? 'Ahorra' : 'Save'} ${(colmadoQuote.total_to_pay - walletSendQuote.total_to_pay).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('pricing.amount') || 'Amount'}</span>
                    <span className="font-medium">${walletSendQuote.amount_usd.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-emerald-200 pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>{t('pricing.youPay') || 'You pay'}</span>
                      <span className="text-emerald-600">${walletSendQuote.total_to_pay.toFixed(2)}</span>
                    </div>
                  </div>
                  {colmadoQuote && (
                    <div className="border-t border-emerald-200 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{t('pricing.exchangeRate') || 'Exchange rate'}</span>
                        <span className="font-medium">1 USD = {colmadoQuote.exchange_rate} DOP</span>
                      </div>
                      <div className="flex justify-between font-semibold text-emerald-700 mt-1">
                        <span>{t('pricing.colmadoReceives') || 'Colmado receives'}</span>
                        <span>RD${colmadoQuote.colmado_receives_dop.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                    <CheckCircle className="w-3 h-3" />
                    {language === 'es' ? 'Sin comisiones al pagar desde tu billetera' : 'No fees when paying from your wallet'}
                  </p>
                </div>
              )}

              {/* Show card payment breakdown when insufficient authorization limit */}
              {colmadoQuote && !priceLoading && wallet?.balance < parseFloat(colmadoAmount || 0) && parseFloat(colmadoAmount || 0) >= 10 && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-2" data-testid="colmado-card-breakdown">
                  <div className="flex items-center gap-2 text-slate-700 text-sm font-medium mb-2">
                    <CreditCard className="w-4 h-4" />
                    {language === 'es' ? 'Pago con tarjeta' : 'Card Payment'}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('pricing.amount')}</span>
                    <span className="font-medium">${colmadoQuote.amount_usd.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('pricing.serviceFee')}</span>
                    <span className="font-medium">${colmadoQuote.service_fee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('pricing.processingFee')}</span>
                    <span className="font-medium">${colmadoQuote.processing_fee.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>{t('pricing.youPay')}</span>
                      <span className="text-emerald-600">${colmadoQuote.total_to_pay.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t('pricing.exchangeRate')}</span>
                      <span className="font-medium">1 USD = {colmadoQuote.exchange_rate} DOP</span>
                    </div>
                    <div className="flex justify-between font-semibold text-emerald-600 mt-1">
                      <span>{t('pricing.colmadoReceives')}</span>
                      <span>RD${colmadoQuote.colmado_receives_dop.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                    <p className="text-xs text-amber-700 flex items-start gap-1">
                      <Info className="w-3 h-3 mt-0.5 shrink-0" />
                      {t('authorization.fundsReleased')}
                    </p>
                  </div>
                </div>
              )}

              {/* Balance check - only show if authorization limit is insufficient for the amount */}
              {parseFloat(colmadoAmount || 0) >= 10 && wallet?.balance < parseFloat(colmadoAmount || 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700">
                    {language === 'es' 
                      ? `Saldo insuficiente ($${wallet?.balance?.toFixed(2)}). Agrega fondos o paga con tarjeta.`
                      : `Insufficient balance ($${wallet?.balance?.toFixed(2)}). Add funds or pay with card.`}
                  </p>
                </div>
              )}

              <Button
                onClick={handleCreateAuthorization}
                disabled={
                  sendLoading || 
                  !selectedMerchant || 
                  !recipientName.trim() || 
                  !colmadoAmount || 
                  parseFloat(colmadoAmount) < 10 ||
                  wallet?.balance < parseFloat(colmadoAmount || 0)
                }
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="create-authorization-btn"
              >
                {sendLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : wallet?.balance >= parseFloat(colmadoAmount || 0) && parseFloat(colmadoAmount || 0) >= 10 ? (
                  <>
                    <Wallet className="w-4 h-4 mr-2" />
                    {t('authorization.createAuthorization')} - ${parseFloat(colmadoAmount).toFixed(2)}
                    <span className="text-emerald-200 ml-1 text-xs">({language === 'es' ? 'sin comisiones' : 'no fees'})</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('authorization.createAuthorization')}
                    {colmadoQuote && ` - $${colmadoQuote.total_to_pay.toFixed(2)}`}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Authorization Details Dialog */}
      <Dialog open={viewAuthOpen} onOpenChange={setViewAuthOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              {t('authorization.referenceId')}: {selectedAuth?.reference_id}
            </DialogTitle>
          </DialogHeader>

          {selectedAuth && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className={`text-center py-2 rounded-lg ${
                selectedAuth.status === 'authorized_pending_redemption' ? 'bg-amber-50 text-amber-700' :
                selectedAuth.status === 'partially_redeemed' ? 'bg-blue-50 text-blue-700' :
                selectedAuth.status === 'fully_redeemed' ? 'bg-emerald-50 text-emerald-700' :
                'bg-red-50 text-red-700'
              }`}>
                <span className="font-medium capitalize">
                  {selectedAuth.status === 'authorized_pending_redemption' ? t('authorization.statusPending') :
                   selectedAuth.status === 'partially_redeemed' ? t('authorization.statusPartial') :
                   selectedAuth.status === 'fully_redeemed' ? t('authorization.statusComplete') :
                   t('authorization.statusExpired')}
                </span>
              </div>

              {/* QR Code if still active */}
              {(selectedAuth.status === 'authorized_pending_redemption' || selectedAuth.status === 'partially_redeemed') && selectedAuth.qr_code && (
                <div className="bg-white border-2 border-slate-200 rounded-xl p-4 text-center">
                  <img 
                    src={selectedAuth.qr_code} 
                    alt="QR Code" 
                    className="w-40 h-40 mx-auto mb-2"
                  />
                  <p className="text-xs text-slate-500 mb-2">{t('authorization.qrInstructions')}</p>
                  {selectedAuth.numeric_code && (
                    <div className="bg-slate-100 rounded-lg p-2">
                      <p className="text-xs text-slate-500">{t('authorization.numericCode')}</p>
                      <p className="font-mono text-xl font-bold tracking-widest">{selectedAuth.numeric_code}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{language === 'es' ? 'Colmado' : 'Colmado'}</span>
                  <span className="font-medium">{selectedAuth.merchant_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('authorization.recipientName')}</span>
                  <span className="font-medium">{selectedAuth.recipient_label}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('pricing.amount')}</span>
                  <span className="font-medium">${selectedAuth.usd_amount?.toFixed(2)} USD</span>
                </div>
                <div className="border-t border-slate-200 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('authorization.authorizedDOP')}</span>
                    <span className="font-medium">RD${selectedAuth.dop_authorized_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('authorization.redeemedDOP')}</span>
                    <span className="font-medium text-blue-600">RD${selectedAuth.dop_redeemed_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>{t('authorization.remainingDOP')}</span>
                    <span className="text-emerald-600">RD${selectedAuth.dop_remaining_amount?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('authorization.expiresAt')}</span>
                    <span className="font-medium">
                      {new Date(selectedAuth.expires_at).toLocaleDateString()} {new Date(selectedAuth.expires_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setViewAuthOpen(false)}
              >
                {language === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Referral Program Modal */}
      <ReferralProgram 
        isOpen={referralOpen} 
        onClose={() => setReferralOpen(false)} 
        token={token} 
      />
      
      {/* New Buy Flow */}
      <BuyForFamily
        isOpen={buyFlowOpen}
        onClose={() => {
          setBuyFlowOpen(false);
          setBuyAgainData(null);
        }}
        onSuccess={(result) => {
          fetchMyAuthorizations();
          fetchTransactions();
        }}
        buyAgainData={buyAgainData}
      />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        language={language}
        activeTab="home"
        onBuyClick={() => {
          setBuyAgainData(null);
          setBuyFlowOpen(true);
        }}
      />
      
      {/* Add padding at bottom for mobile nav */}
      <div className="h-20 md:hidden" />
    </div>
  );
}
